import React, { useEffect, useMemo } from 'react';
import {
  Card,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Skeleton,
} from '@mui/material';
import { getProjectColorByName } from '../../../../../utils/constants/pointColors';
import Chart from 'react-apexcharts';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getProjectWisePerformance } from '../../../../../redux/features/poinTrackerSlice';

export default function ProjectAnalyticsCard({ fromDate, toDate, selectedTeam }) {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.projectWisePerformance);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getProjectWisePerformance(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const projectData = useMemo(() => {
    const data = responseData?.data || [];
    return data.map((item, index) => ({
      project: item.projectName,
      admissions: item.admissions,
      revenue: item.revenue,
      arpu: item.arpu,
      trends: item.trends,
    }));
  }, [responseData]);

  // ✅ Chart from API
  // const chartConfigs = useMemo(() => {
  //   return projectData.map((item) => ({
  //     options: {
  //       chart: { type: 'bar', height: 120, toolbar: { show: false } },
  //       plotOptions: { bar: { borderRadius: 4 } },
  //       colors: ['#ff4444', '#00aa00', '#4477ff'],
  //       dataLabels: { enabled: false },
  //       grid: { show: false },
  //       xaxis: { categories: [''] },
  //       yaxis: { show: false },
  //     },
  //     series: [
  //       {
  //         name: item.project,
  //         data: [item.admissions, item.revenue, item.arpu],
  //       },
  //     ],
  //   }));
  // }, [projectData]);
  const chartConfig = useMemo(() => {
    return {
      options: {
        chart: {
          type: 'bar',
          height: 300,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 5,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent'],
        },
        xaxis: {
          categories: projectData.map((item) => item.project), // 👈 project names
        },
        yaxis: {
          title: {
            text: 'Values',
          },
        },
        colors: ['#ff4444', '#00aa00', '#4477ff'], // Admissions, Revenue, ARPU
        tooltip: {
          y: {
            formatter: (val) => `₹ ${val}`,
          },
        },
      },
      series: [
        {
          name: 'Admissions',
          data: projectData.map((item) => item.admissions),
        },
        {
          name: 'Revenue',
          data: projectData.map((item) => item.revenue),
        },
        {
          name: 'ARPU',
          data: projectData.map((item) => item.arpu),
        },
      ],
    };
  }, [projectData]);
  const getTrend = (value) => {
    if (value === undefined || value === null) return null;

    return value >= 0 ? (
      <Box component="span" sx={{ color: 'green', fontSize: 12 }}>
        ↑
      </Box>
    ) : (
      <Box component="span" sx={{ color: 'red', fontSize: 12 }}>
        ↓
      </Box>
    );
  };

  return (
    <Card
      sx={{
        width: '100%',
        p: 3,
        borderRadius: 4,
        backgroundColor: '#f4f6f8',
        boxShadow: 3,
      }}
    >
      {loading ? (
        <Skeleton width="50%" height={25} sx={{ mb: 2 }} />
      ) : (
        <Typography fontWeight="bold" mb={2}>
          Project Wise Performance Analytics
        </Typography>
      )}

      {/* Table */}
      <Box sx={{ ml: -1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Project', 'Admissions', 'Revenue', 'ARPU'].map((head, i) => (
                <TableCell key={i}>{loading ? <Skeleton width="70%" /> : <b>{head}</b>}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(4)].map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : projectData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No Data Available
                </TableCell>
              </TableRow>
            ) : (
              projectData.map((row, index) => {
                const { color } = getProjectColorByName(row.project);

                return (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ color, fontWeight: 600 }}>{row.project}</Box>
                    </TableCell>

                    <TableCell>
                      {row.admissions} {getTrend(row.trends?.admissions)}
                    </TableCell>

                    <TableCell>
                      ₹{row.revenue.toLocaleString()} {getTrend(row.trends?.revenue)}
                    </TableCell>

                    <TableCell>
                      ₹{row.arpu.toLocaleString()} {getTrend(row.trends?.arpu)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        {loading ? (
          [...Array(3)].map((_, i) => <Skeleton key={i} width={80} height={20} />)
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'red', borderRadius: '50%' }} />
              <Typography variant="caption">Admissions</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'green', borderRadius: '50%' }} />
              <Typography variant="caption">Revenue</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: 'blue', borderRadius: '50%' }} />
              <Typography variant="caption">ARPU</Typography>
            </Box>
          </>
        )}
      </Box>

      {/* <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-around', mt: 3, mb: 2 }}>
        {chartConfigs.map((config, index) => (
          <Box key={index} sx={{ flex: 1 }}>
            <Chart options={config.options} series={config.series} type="bar" height={120} />
          </Box>
          
        ))}
      </Box> */}
      <Box sx={{ mt: 3 }}>
        {loading ? (
          <Skeleton variant="rounded" height={300} />
        ) : (
          <Chart options={chartConfig.options} series={chartConfig.series} type="bar" height={300} />
        )}
      </Box>
    </Card>
  );
}
